import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Users, 
    Percent, 
    CreditCard, 
    Shield, 
    Star, 
    Clock, 
    Headset, 
    UserCheck, 
    Sparkles,
    ArrowRight,
    DollarSign
} from 'lucide-react';
import FAQ from './FAQ';
import './HowItWorksSection.css';

// Animated text that reveals character by character
const AnimatedText = ({ text, className, delay = 0, duration = 0.05, ...props }) => {
    const [isMobile, setIsMobile] = useState(false);
    
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    
    // Skip character animation on mobile for better performance
    if (isMobile) {
        return (
            <motion.span 
                className={`hiw-animated-text ${className || ''}`}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay }}
                {...props}
            >
                {text}
            </motion.span>
        );
    }
    
    return (
        <motion.span className={`hiw-animated-text ${className || ''}`} {...props}>
            {text.split('').map((char, index) => (
                <motion.span
                    key={index}
                    className="hiw-animated-char"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                        duration: 0.4, 
                        delay: delay + index * duration,
                        ease: [0.22, 1, 0.36, 1]
                    }}
                >
                    {char === ' ' ? '\u00A0' : char}
                </motion.span>
            ))}
        </motion.span>
    );
};

// Interactive tilt card that responds to mouse position
const TiltCard = ({ children, intensity = 15, ...props }) => {
    const cardRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const rotateX = useRef(0);
    const rotateY = useRef(0);
    
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1024 || 'ontouchstart' in window);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    
    const handleMouseMove = (e) => {
        if (!cardRef.current || !isHovered || isMobile) return;
        
        const rect = cardRef.current.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        // Calculate rotation values based on mouse position
        const newRotateY = ((mouseX - centerX) / centerX) * intensity;
        const newRotateX = ((centerY - mouseY) / centerY) * intensity;
        
        rotateX.current = newRotateX;
        rotateY.current = newRotateY;
        
        if (cardRef.current) {
            cardRef.current.style.transform = `perspective(1200px) rotateX(${rotateX.current}deg) rotateY(${rotateY.current}deg)`;
        }
    };
    
    const resetRotation = () => {
        setIsHovered(false);
        rotateX.current = 0;
        rotateY.current = 0;
        
        if (cardRef.current) {
            cardRef.current.style.transform = `perspective(1200px) rotateX(0deg) rotateY(0deg)`;
        }
    };
    
    return (
        <div
            ref={cardRef}
            style={{ 
                transformStyle: "preserve-3d",
                transition: "transform 0.2s ease-out"
            }}
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={resetRotation}
            onTouchStart={() => {}}
            onTouchEnd={resetRotation}
            {...props}
        >
            {children}
            <div className="hiw-tilt-card-shine"></div>
        </div>
    );
};

// Enhanced animated icon with complex animations
const AnimatedIcon = ({ icon: Icon, color = "#00D1E0", size = 36, ...props }) => {
    const [iconSize, setIconSize] = useState(size);
    
    useEffect(() => {
        const handleResize = () => {
            setIconSize(window.innerWidth < 768 ? 28 : size);
        };
        
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [size]);
    
    return (
        <div className="hiw-icon-wrapper" {...props}>
            <motion.div
                className="hiw-icon-container"
                whileHover={{ 
                    scale: 1.15, 
                    rotate: [0, 5, -5, 0],
                    boxShadow: '0 15px 30px rgba(0, 209, 224, 0.3), 0 0 15px rgba(255, 71, 117, 0.15)'
                }}
                transition={{ 
                    rotate: { repeat: 0, duration: 0.5 },
                    boxShadow: { duration: 0.2 }
                }}
            >
                <Icon size={iconSize} color={color} className="hiw-icon" />
                <div className="hiw-icon-glow"></div>
                <div className="hiw-icon-particles">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className={`hiw-icon-particle particle-${i}`}></div>
                    ))}
                </div>
            </motion.div>
        </div>
    );
};

// Floating 3D blob shapes component
const FloatingBlobs = () => {
    const [isMobile, setIsMobile] = useState(false);
    
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    
    return (
        <div className="hiw-blob-container">
            <div className={`hiw-blob blob-1 ${isMobile ? 'mobile' : ''}`}></div>
            <div className={`hiw-blob blob-2 ${isMobile ? 'mobile' : ''}`}></div>
            {!isMobile && (
                <>
                    <div className="hiw-blob blob-3"></div>
                    <div className="hiw-blob blob-4"></div>
                </>
            )}
        </div>
    );
};

// Enhanced motion button with advanced effects
const MotionButton = ({ children, primary, ...props }) => (
    <motion.button
        className={`hiw-motion-button ${primary ? 'primary' : 'secondary'}`}
        whileHover={{ 
            scale: 1.05,
            boxShadow: primary 
                ? '0 15px 30px rgba(255, 71, 117, 0.4), 0 0 0 2px rgba(255, 255, 255, 0.15)' 
                : '0 10px 25px rgba(0, 209, 224, 0.25), 0 0 0 2px rgba(0, 209, 224, 0.3)'
        }}
        whileTap={{ scale: 0.97 }}
        transition={{ type: 'spring', stiffness: 400, damping: 17 }}
        {...props}
    >
        <span className="hiw-button-content">{children}</span>
        {primary && (
            <>
                <span className="hiw-button-icon">
                    <ArrowRight size={16} />
                </span>
                <div className="hiw-button-glow"></div>
            </>
        )}
    </motion.button>
);

// Helper function for intersection observer
const useInView = (ref, options = {}) => {
    const [isInView, setIsInView] = useState(false);
    
    useEffect(() => {
        if (!ref.current) return;
        
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsInView(entry.isIntersecting);
                
                // If once is true and element is in view, disconnect
                if (options.once && entry.isIntersecting) {
                    observer.disconnect();
                }
            },
            {
                threshold: options.amount || 0
            }
        );
        
        observer.observe(ref.current);
        
        return () => {
            if (ref.current) {
                observer.unobserve(ref.current);
            }
        };
    }, [ref, options.once, options.amount]);
    
    return isInView;
};

// Enhanced feature card component
const FeatureCard = ({ icon: Icon, title, description, index }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: false, amount: 0.3 });
    
    return (
        <motion.div
            ref={ref}
            className="hiw-feature-card-wrapper"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7, delay: 0.2 + index * 0.15 }}
        >
            <TiltCard>
                <motion.div 
                    className="hiw-feature-card"
                    whileHover={{ 
                        y: -5,
                        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.15) inset, 0 0 30px rgba(0, 209, 224, 0.15) inset'
                    }}
                >
                    <AnimatedIcon icon={Icon} />
                    <h3 className="hiw-feature-title">{title}</h3>
                    <p className="hiw-feature-description">{description}</p>
                </motion.div>
            </TiltCard>
        </motion.div>
    );
};

// Testimonial card component with fixed star rendering
const TestimonialCard = ({ name, saving, quote, index }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: false, amount: 0.3 });
    
    // Get initials from name
    const getInitials = (name) => {
        return name.split(' ').map(n => n[0]).join('');
    };
    
    // Reduce animation complexity on mobile
    const [isMobile, setIsMobile] = useState(false);
    
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    
    return (
        <motion.div
            ref={ref}
            className="hiw-testimonial-card-wrapper"
            initial={{ opacity: 0, x: isMobile ? 0 : (index % 2 === 0 ? -30 : 30), y: 20 }}
            animate={isInView ? { opacity: 1, x: 0, y: 0 } : { opacity: 0, x: isMobile ? 0 : (index % 2 === 0 ? -30 : 30), y: 20 }}
            transition={{ duration: 0.7, delay: 0.2 + index * 0.15 }}
        >
            <TiltCard intensity={10}>
                <motion.div 
                    className="hiw-testimonial-card"
                    whileHover={{ 
                        y: -8,
                        boxShadow: '0 30px 60px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.15) inset, 0 0 20px rgba(0, 209, 224, 0.1) inset'
                    }}
                >
                    <motion.div 
                        className="hiw-testimonial-avatar"
                        whileHover={{ 
                            scale: 1.1,
                            boxShadow: '0 15px 30px rgba(0, 0, 0, 0.25), 0 0 15px rgba(0, 209, 224, 0.4)'
                        }}
                    >
                        {getInitials(name)}
                        <div className="hiw-avatar-glow"></div>
                    </motion.div>
                    
                    <div className="hiw-star-container">
                        {[...Array(5)].map((_, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, scale: 0, rotate: -30 }}
                                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                                transition={{ 
                                    delay: isMobile ? 0.3 + i * 0.05 : 0.2 * index + 0.8 + i * 0.1, 
                                    type: "spring" 
                                }}
                            >
                                <Star 
                                    size={18} 
                                    className="hiw-star-icon" 
                                    fill="#FFD700" 
                                    color="#FFD700" 
                                />
                            </motion.div>
                        ))}
                    </div>
                    
                    <p className="hiw-testimonial-quote">"{quote}"</p>
                    <p className="hiw-testimonial-name">{name}</p>
                    
                    <motion.div
                        className="hiw-saving-badge"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: isMobile ? 0.5 : 0.2 * index + 1.2, type: "spring" }}
                    >
                        <p className="hiw-saving-amount">Saving {saving}</p>
                    </motion.div>
                </motion.div>
            </TiltCard>
        </motion.div>
    );
};

// Protection feature component
const ProtectionFeature = ({ icon: Icon, text, index }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: false, amount: 0.3 });
    
    return (
        <motion.div
            ref={ref}
            className="hiw-protection-item"
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
            transition={{ duration: 0.7, delay: index * 0.15 + 0.2 }}
            whileHover={{ 
                boxShadow: '0 15px 30px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.15) inset, 0 0 20px rgba(0, 209, 224, 0.08) inset',
                x: 5
            }}
        >
            <div className="hiw-protection-icon-container">
                <Icon size={24} className="hiw-protection-icon" />
            </div>
            <p className="hiw-protection-text">{text}</p>
        </motion.div>
    );
};

// Main Modern How It Works Section Component
const HowItWorksSection = () => {
    const [tabValue, setTabValue] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const sectionRef = useRef(null);
    
    // Handle mobile detection
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    
    // Handle tab changes
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };
    
    // Navigate to all circles page
    const navigateToAllCircles = () => {
        window.location.href = '/all-circles';
    };
    
    // Navigate to about us page
    const navigateToAboutUs = () => {
        window.location.href = '/about-us';
    };
    
    // Scroll trigger for fade-in animations
    const handleScroll = useCallback(() => {
        const elements = document.querySelectorAll('.hiw-fade-in-element');
        elements.forEach((el) => {
            const rect = el.getBoundingClientRect();
            const isVisible = rect.top < window.innerHeight - 100 && rect.bottom >= 0;
            if (isVisible) {
                el.classList.add('hiw-fade-in');
            }
        });
    }, []);
    
    // Set up scroll listener and check hash on load
    useEffect(() => {
        if (typeof window === 'undefined') return;
        
        window.addEventListener('scroll', handleScroll);
        handleScroll();
        
        const handleHashChange = () => {
            if (window.location.hash === '#faq') {
                setTabValue(1);
                setTimeout(() => {
                    const faqSection = document.getElementById('faq');
                    if (faqSection) {
                        faqSection.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 100);
            }
        };
        
        handleHashChange();
        window.addEventListener('hashchange', handleHashChange);
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [handleScroll]);
    
    // Content data
    const overviewSteps = [
        {
            icon: Users,
            title: "Join a Circle",
            description: "Connect with others to form a group plan. The more people in your circle, the bigger your savings!"
        },
        {
            icon: Percent,
            title: "Unlock Group Discounts",
            description: "Access premium plans at discounted rates."
        },
        {
            icon: CreditCard,
            title: "Save on Your Bill",
            description: "Enjoy lower monthly payments. On average, Circledin members save 45% on their mobile plans."
        }
    ];

    const testimonials = [
        {
            name: "Sarah J.",
            saving: "$35/month",
            quote: "I was skeptical at first, but Circledin has been a game-changer for my budget!"
        },
        {
            name: "Michael T.",
            saving: "$50/month",
            quote: "Creating a circle was easy, and now my entire family is saving on their phone bills."
        },
        {
            name: "Emily R.",
            saving: "$40/month",
            quote: "The customer support is fantastic. They handled everything when I switched to a group plan."
        }
    ];

    const protectionFeatures = [
        {
            icon: Shield,
            text: "Circledin manages all payments, ensuring your service is never interrupted, even if a circle member is late."
        },
        {
            icon: Clock,
            text: "We continuously monitor for better deals and notify you of additional saving opportunities."
        },
        {
            icon: Headset,
            text: "Our team handles all customer service issues, saving you time and hassle."
        },
        {
            icon: UserCheck,
            text: "All Account Owners are verified and trusted, ensuring the security of your circle."
        }
    ];

    return (
        <div className="hiw-wrapper" ref={sectionRef}>
            {/* Background elements */}
            <div className="hiw-noise-texture"></div>
            <FloatingBlobs />
            <div className="hiw-gradient-overlay"></div>
            
            <div className="hiw-particles">
                {[...Array(isMobile ? 15 : 30)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="hiw-particle"
                        style={{
                            width: Math.random() * (isMobile ? 6 : 8) + 2,
                            height: Math.random() * (isMobile ? 6 : 8) + 2,
                            x: Math.random() * 100 + "%",
                            y: Math.random() * 100 + "%",
                            opacity: Math.random() * 0.6 + 0.2,
                            background: i % 3 === 0 ? "rgba(255, 71, 117, 0.8)" : "rgba(255, 255, 255, 0.8)"
                        }}
                        animate={{
                            y: [0, -100],
                            x: [0, Math.random() * 40 - 20],
                            opacity: [0, 1, 0]
                        }}
                        transition={{
                            duration: Math.random() * (isMobile ? 15 : 10) + (isMobile ? 15 : 10),
                            repeat: Infinity,
                            delay: Math.random() * 5
                        }}
                    />
                ))}
            </div>

            <div className="hiw-container">
                <div className="hiw-content-wrapper">
                    {/* Header with parallax effect */}
                    <div className="hiw-header">
                        <div className="hiw-title-container">
                            <h1 className="hiw-main-title">
                                <AnimatedText text="How Circledin Works" delay={0.3} />
                            </h1>
                            
                            <div className="hiw-title-decoration">
                                <motion.div 
                                    className="hiw-title-line"
                                    initial={{ scaleX: 0 }}
                                    animate={{ scaleX: 1 }}
                                    transition={{ delay: 1, duration: 1, ease: [0.23, 1, 0.32, 1] }}
                                />
                                <motion.div 
                                    className="hiw-title-dot"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 1.8, duration: 0.5, type: 'spring' }}
                                />
                            </div>
                        </div>
                        
                        {/* Description text with improved color */}
                        <h2 className="hiw-subtitle hiw-fade-in-element">
                            Circledin connects you with others to share group mobile plans, helping everyone save money.
                        </h2>

                        {/* Added spacing div between text and tabs */}
                        <div className="hiw-text-tabs-spacer"></div>

                        <div className="hiw-tabs-container hiw-fade-in-element">
                            <div className="hiw-tabs">
                                <button 
                                    className={`hiw-tab ${tabValue === 0 ? 'active' : ''}`}
                                    onClick={() => handleTabChange(null, 0)}
                                    aria-label="Show How It Works content"
                                >
                                    <motion.span 
                                        whileHover={{ 
                                            scale: 1.05,
                                            color: 'var(--hiw-primary-color)'
                                        }}
                                    >
                                        How It Works
                                    </motion.span>
                                </button>
                                
                                <button 
                                    className={`hiw-tab ${tabValue === 1 ? 'active' : ''}`}
                                    onClick={() => handleTabChange(null, 1)}
                                    aria-label="Show FAQ content"
                                >
                                    <motion.span 
                                        whileHover={{ 
                                            scale: 1.05,
                                            color: 'var(--hiw-primary-color)'
                                        }}
                                    >
                                        FAQ
                                    </motion.span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Add spacing div between tabs and content */}
                    <div className="hiw-tabs-content-spacer"></div>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={tabValue}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.5 }}
                            className="hiw-tab-content"
                        >
                            {tabValue === 0 && (
                                <>
                                    <div>
                                        <div className="hiw-overview-grid">
                                            {overviewSteps.map((step, index) => (
                                                <FeatureCard 
                                                    key={index}
                                                    icon={step.icon}
                                                    title={step.title}
                                                    description={step.description}
                                                    index={index}
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    <div className="hiw-testimonials-section">
                                        <h2 className="hiw-section-title hiw-fade-in-element">
                                            <AnimatedText text="What Our Users Say" delay={0.3} duration={0.03} />
                                        </h2>
                                        
                                        <div className="hiw-testimonials-grid">
                                            {testimonials.map((testimonial, index) => (
                                                <TestimonialCard 
                                                    key={index}
                                                    name={testimonial.name}
                                                    saving={testimonial.saving}
                                                    quote={testimonial.quote}
                                                    index={index}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    
                                    <div className="hiw-protection-section">
                                        <h2 className="hiw-section-title hiw-fade-in-element">
                                            <AnimatedText text="How You're Protected" delay={0.3} duration={0.03} />
                                        </h2>
                                        
                                        <div className="hiw-protection-grid">
                                            {protectionFeatures.map((feature, index) => (
                                                <ProtectionFeature
                                                    key={index}
                                                    icon={feature.icon}
                                                    text={feature.text}
                                                    index={index}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    
                                    <motion.div 
                                        className="hiw-cta-container hiw-fade-in-element"
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: isMobile ? 0.8 : 1.5, duration: 0.8 }}
                                    >
                                        <MotionButton primary onClick={navigateToAllCircles}>
                                            Start Saving Now!
                                        </MotionButton>
                                        
                                        <MotionButton onClick={navigateToAboutUs}>
                                            Learn More
                                        </MotionButton>
                                    </motion.div>
                                </>
                            )}

                            {tabValue === 1 && (
                                <section id="faq">
                                    <FAQ />
                                </section>
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
            
            {/* Floating decorative elements */}
            {!isMobile && (
                <div className="hiw-decoration-elements">
                    <motion.div 
                        className="hiw-floating-element element-1"
                        animate={{ 
                            y: [0, -15, 0],
                            rotate: [0, 5, 0]
                        }}
                        transition={{ 
                            duration: 5,
                            repeat: Infinity,
                            repeatType: "mirror",
                        }}
                    >
                        <Sparkles size={32} color="rgba(0, 209, 224, 0.7)" />
                    </motion.div>
                    
                    <motion.div 
                        className="hiw-floating-element element-2"
                        animate={{ 
                            y: [0, 20, 0],
                            rotate: [0, -8, 0]
                        }}
                        transition={{ 
                            duration: 6,
                            repeat: Infinity,
                            repeatType: "mirror",
                            delay: 1
                        }}
                    >
                        <DollarSign size={24} color="rgba(0, 209, 224, 0.6)" />
                    </motion.div>
                    
                    <motion.div 
                        className="hiw-floating-element element-3"
                        animate={{ 
                            y: [0, -10, 0],
                            x: [0, 10, 0],
                            rotate: [0, 15, 0]
                        }}
                        transition={{ 
                            duration: 7,
                            repeat: Infinity,
                            repeatType: "mirror",
                            delay: 2
                        }}
                    >
                        <Star size={20} color="rgba(255, 71, 117, 0.7)" />
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default HowItWorksSection;